import { systemSettings } from '@/mocks/settings';
import { formatTime } from '@/lib/format';
import type { LaundryAddress, LaundryProfile } from '@/types';

/** Translator signature (react-i18next's t). */
type TFn = (key: string) => string;

const cityNameOf = (cityId: string): string =>
  systemSettings.cities.find((city) => city.id === cityId)?.name ?? cityId;

/** One readable line for a laundry address (city · district · street · building). */
export function formatLaundryAddress(address: LaundryAddress): string {
  return [cityNameOf(address.cityId), address.district, address.street, address.building]
    .filter((part) => Boolean(part && part.trim()))
    .join(' - ');
}

/** Google Maps embed URL for an address (query is URL-encoded). */
export function buildMapEmbedUrl(address: LaundryAddress): string {
  const query = formatLaundryAddress(address);
  return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
}

export interface ProfileFieldDiff {
  key: string;
  labelKey: string;
  /** Approved value at submission time. */
  from: string;
  /** Requested value. */
  to: string;
  /** Long text (description) should wrap instead of truncating. */
  multiline?: boolean;
  /** Logo row rendered as a neutral note, not a raw data URL. */
  isLogo?: boolean;
}

const hoursLabel = (hours: number | null, t: TFn): string =>
  hours == null ? t('settings.value.notSet') : `${hours} ${t('settings.unit.hours')}`;

const districtsLabel = (districts: string[], t: TFn): string =>
  districts.length ? districts.join('، ') : t('settings.coverage.none');

const timeRangeLabel = (from: string, to: string, lang: string): string =>
  `${formatTime(from, lang)} - ${formatTime(to, lang)}`;

/**
 * Builds the list of fields that actually changed between the approved data and
 * the requested data. Used for the review/approved/rejected views.
 */
export function buildProfileDiff(
  previous: LaundryProfile,
  proposed: LaundryProfile,
  t: TFn,
  lang: string,
): ProfileFieldDiff[] {
  const diffs: ProfileFieldDiff[] = [];

  const push = (
    key: string,
    labelKey: string,
    from: string,
    to: string,
    extra?: Pick<ProfileFieldDiff, 'multiline' | 'isLogo'>,
  ) => {
    if (from !== to) diffs.push({ key, labelKey, from, to, ...extra });
  };

  push('name', 'settings.field.name', previous.name, proposed.name);
  push('description', 'settings.field.description', previous.description, proposed.description, {
    multiline: true,
  });

  if (previous.logoUrl !== proposed.logoUrl) {
    diffs.push({
      key: 'logoUrl',
      labelKey: 'settings.field.logo',
      from: t('settings.value.currentLogo'),
      to: t('settings.value.newLogo'),
      isLogo: true,
    });
  }

  push('phone', 'settings.field.phone', previous.phone, proposed.phone);
  push(
    'address',
    'settings.field.address',
    formatLaundryAddress(previous.address),
    formatLaundryAddress(proposed.address),
  );
  push(
    'coverageDistricts',
    'settings.field.coverage',
    districtsLabel(previous.coverageDistricts, t),
    districtsLabel(proposed.coverageDistricts, t),
  );
  push(
    'workingHours',
    'settings.field.workingHours',
    timeRangeLabel(previous.fromTime, previous.toTime, lang),
    timeRangeLabel(proposed.fromTime, proposed.toTime, lang),
  );
  push(
    'normalHours',
    'settings.field.normalDuration',
    hoursLabel(previous.normalHours, t),
    hoursLabel(proposed.normalHours, t),
  );
  push(
    'urgentHours',
    'settings.field.urgentDuration',
    hoursLabel(previous.urgentHours, t),
    hoursLabel(proposed.urgentHours, t),
  );

  return diffs;
}

/** True when at least one field differs between the approved and requested data. */
export function hasProfileChanges(previous: LaundryProfile, proposed: LaundryProfile): boolean {
  const identity: TFn = (key) => key;
  return buildProfileDiff(previous, proposed, identity, 'ar').length > 0;
}